import React from "react";
import "./comparisonTable.css";

interface TableRow {
  label: string;
  values: (number | string)[];
  unit?: string;
}

interface ComparisonTableProps {
  title: string;
  tableData: TableRow[];
  colors: string[];
  legendLabels: string[];
  className?: string;
}

const ComparisonTable: React.FC<ComparisonTableProps> = ({
  title,
  tableData,
  colors,
  legendLabels,
  className = "",
}) => {
  const darkenColor = (color: string): string => {
    if (color.startsWith("var(")) {
      if (color.includes("--color-light-gray")) return "#d0cfcf";
      if (color.includes("--color-gray")) return "#b8b8b8";
      if (color.includes("--color-dark-blue")) return "#1e3c5a";
      if (color.includes("--color-blue")) return "#3d6b9c";
      if (color.includes("--color-light-blue")) return "#2a8fa6";
      if (color.includes("--color-teal")) return "#026d7a";
      if (color.includes("--color-green")) return "#1a8b60";
      if (color.includes("--color-orange")) return "#c5471a";
      if (color.includes("--color-yellow")) return "#b8750a";
      return "#999999";
    }
    return color;
  };

  const shapeColor = darkenColor("var(--color-light-gray)");

  const getValueColor = (value: number | string): string => {
    const numericValue =
      typeof value === "string"
        ? parseFloat(value.replace(/[^\d.-]/g, ""))
        : value;

    if (numericValue > 0) {
      return "var(--color-green)";
    } else if (numericValue < 0) {
      return "var(--color-orange)";
    } else {
      return "var(--color-dark-blue)";
    }
  };

  return (
    <div className={`comparisonTable-container ${className}`}>
      <svg
        className="comparisonTable-svg-top-left"
        width="160"
        height="40"
        viewBox="0 0 111 19"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M65.0082 15.7865C100.199 12.0577 108.741 3.83731 110.135 0.914062L0.00927734 0.914071L0.00927896 16.9908C8.37324 18.1601 30.8655 19.4042 65.0082 15.7865Z"
          fill={shapeColor}
        />
      </svg>

      <svg
        className="comparisonTable-svg-bottom-right"
        width="280"
        height="70"
        viewBox="0 0 186 35"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_compTable)">
          <path
            d="M102.58 15.493C38.5252 15.493 7.8007 28.1851 0.445312 34.5312H185.801V0.685547C184.75 5.62137 166.635 15.493 102.58 15.493Z"
            fill={shapeColor}
          />
        </g>
        <defs>
          <clipPath id="clip0_compTable">
            <path
              d="M0.445312 0.685547H185.801V24.5312C185.801 30.0541 181.324 34.5312 175.801 34.5312H0.445312V0.685547Z"
              fill="white"
            />
          </clipPath>
        </defs>
      </svg>

      {title && (
        <div className="comparisonTable-header">
          <span className="comparisonTable-subtitle">Comparison table</span>
          <h3 className="comparisonTable-title">{title.toUpperCase()}</h3>
        </div>
      )}

      <div className="comparisonTable-content">
        <div className="comparisonTable-table-container">
          <table className="comparisonTable-table">
            <thead>
              <tr>
                <th className="comparisonTable-table-header-empty"></th>
                {legendLabels.map((label, index) => (
                  <th key={index} className="comparisonTable-table-header">
                    <div className="comparisonTable-header-cell">
                      <div
                        className="comparisonTable-color-dot"
                        style={{ backgroundColor: colors[index] }}
                      ></div>
                      <span className="comparisonTable-header-label">{label}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td className="comparisonTable-table-label">{row.label}</td>
                  {row.values.map((value, valueIndex) => (
                    <td
                      key={valueIndex}
                      className="comparisonTable-table-value"
                      style={{ color: getValueColor(value) }}
                    >
                      {value}
                      {row.unit || ""}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ComparisonTable;
